import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import PaymentMethodIcons from './PaymentMethodIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FullPackCard = ({ variant = 'landing', onPurchaseComplete }) => {
  const [packInfo, setPackInfo] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchPackInfo();
    checkPurchaseStatus();
  }, []);

  const fetchPackInfo = async () => {
    try {
      const response = await api.get(`/fullpack/info`);
      setPackInfo(response.data);
    } catch (error) {
      console.error('Error fetching pack info:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const response = await api.get(`/fullpack/status`, {
        withCredentials: true
      });
      setPurchaseStatus(response.data);
    } catch (error) {
      // User not logged in or error - that's ok
      console.log('Not logged in or error checking status');
    }
  };

  const handlePurchase = async () => {
    try {
      setPurchasing(true);
      
      // Check if user is authenticated first
      try {
        await api.get('/auth/me', { withCredentials: true });
      } catch (authError) {
        // Not authenticated - redirect to login
        localStorage.setItem('wtpl_intent', 'full_pack');
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        return;
      }
      
      const response = await api.post(
        `/fullpack/create-checkout`,
        {},
        { 
          withCredentials: true,
          timeout: 30000 
        }
      );
      
      // Redirect to Stripe checkout (mobile-friendly)
      if (response.data.url) {
        setTimeout(() => {
          window.location.assign(response.data.url);
        }, 100);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setPurchasing(false);
      if (error.response?.status === 401) {
        // Redirect to login
        localStorage.setItem('wtpl_intent', 'full_pack');
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Error al procesar la compra. Intenta de nuevo.');
      }
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      
      const response = await api.post(
        `/fullpack/download`,
        {},
        { withCredentials: true }
      );
      
      // Backend now returns a download URL to the pre-made ZIP
      if (response.data.download_url) {
        // Open the Google Drive download link in a new tab
        window.open(response.data.download_url, '_blank');
      }
      
      if (onPurchaseComplete) onPurchaseComplete();
    } catch (error) {
      console.error('Error downloading:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Error al descargar. Intenta de nuevo.');
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#252525] border-[#00D9FF]/30">
        <CardContent className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00D9FF] mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  const hasPurchased = purchaseStatus?.purchased;

  // Landing variant - full card (GOLD Premium Style)
  if (variant === 'landing') {
    return (
      <div className="bg-[#141414] border-2 border-[#00D9FF] rounded-2xl p-6 relative shadow-lg shadow-[#00D9FF]/20 flex flex-col h-full">
        {/* Premium Badge */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-[#00D9FF] to-[#00B8D9] text-black text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">PREMIUM</span>
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-bold mb-2 mt-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>WTPL Full System</h3>
        <p className="text-sm text-[#B3B3B3] mb-3">Biblioteca Musical de DJLYO</p>
        
        {/* Urgency Banner */}
        <div className="bg-[#00D9FF]/10 border border-[#00D9FF]/40 rounded-lg p-2 mb-3">
          <p className="text-[#00D9FF] font-bold text-xs">PRECIO LANZAMIENTO</p>
          <p className="text-[#00B8D9] text-xs">Tiempo limitado</p>
          <p className="text-[#00D9FF] text-xs">Edición Premium</p>
        </div>
        
        {/* Price with strikethrough */}
        <div className="mb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-base text-[#666] line-through">$197</span>
            <span className="text-4xl font-black text-[#00D9FF]">$97</span>
            <span className="text-[#888] text-sm">USD</span>
          </div>
          <p className="text-xs text-[#666] mt-1">Pago único • Acceso de por vida</p>
        </div>
        
        {/* Features */}
        <ul className="space-y-3 mb-6 flex-1">
          {[
            "Sistema DJ listo para tocar",
            "Crates organizados por energía",
            "+20 años de curaduría",
            "Edits exclusivos incluidos",
            "Organización para eventos"
          ].map((feature) => (
            <li key={feature} className="flex items-center gap-3 text-sm">
              <svg className="w-4 h-4 text-[#00D9FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <span className="text-[#B3B3B3]">{feature}</span>
            </li>
          ))}
        </ul>
        
        {/* Button */}
        <div className="mt-auto space-y-2">
          {hasPurchased ? (
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] hover:opacity-90 font-bold py-4 rounded-xl text-black"
            >
              {downloading ? 'Preparando...' : 'Descargar'}
            </Button>
          ) : (
            <Button
              onClick={handlePurchase}
              disabled={purchasing}
              className="w-full bg-gradient-to-r from-[#00D9FF] via-[#00C5E5] to-[#00B8D9] hover:opacity-90 font-bold py-4 rounded-xl text-black border border-[#00D9FF]/50"
              data-testid="buy-fullpack-btn"
            >
              {purchasing ? 'Procesando...' : 'Obtener Full System – $97'}
            </Button>
          )}
          {hasPurchased ? <div className="py-3"></div> : <PaymentMethodIcons className="pt-2 pb-1" showLabel={false} />}
        </div>
      </div>
    );
  }

  // Dashboard variant - compact (GOLD Premium Style)
  return (
    <div className="relative bg-[#0D0D0D] border border-[#00D9FF]/30 rounded-xl p-4 overflow-hidden hover:border-[#00D9FF]/50 transition-all">
      {/* Gold shimmer overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#00D9FF]/[0.02] via-transparent to-[#00D9FF]/[0.02] pointer-events-none"></div>
      
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00D9FF] via-[#00C5E5] to-[#0088FF] flex items-center justify-center shadow-lg shadow-[#00D9FF]/20 flex-shrink-0">
            <span className="text-xl">
              <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
            </span>
          </div>
          <div>
            <h4 className="font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              WTPL Full System
              <span className="text-[10px] bg-gradient-to-r from-[#00D9FF] to-[#00B8D9] text-black px-2 py-0.5 rounded font-bold">PREMIUM</span>
            </h4>
            <p className="text-xs text-[#666]">
              50K+ canciones • Todos los géneros • Descarga ZIP completa
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xl font-bold text-[#00D9FF]">${packInfo?.price || 97}</p>
            <p className="text-[10px] text-[#666]">USD • Pago único</p>
          </div>
          
          {hasPurchased ? (
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] hover:opacity-90 font-bold rounded-lg px-4 text-black"
            >
              {downloading ? 'Espera...' : 'Descargar'}
            </Button>
          ) : (
            <Button
              onClick={handlePurchase}
              disabled={purchasing}
              className="bg-gradient-to-r from-[#00D9FF] via-[#00C5E5] to-[#00B8D9] hover:opacity-90 font-bold rounded-lg px-4 text-black"
            >
              {purchasing ? '...' : 'Obtener'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullPackCard;
